import { cn } from "@/lib/utils";
import * as React from "react";
import { Eyebrow } from "./Eyebrow";

export interface SectionHeadingProps {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  /** One or two sentences. Anything longer belongs in the body, not here. */
  lead?: React.ReactNode;
  /** Right-hand action, e.g. a "View all" link. Hidden on narrow screens. */
  action?: React.ReactNode;
  as?: "h1" | "h2" | "h3";
  align?: "left" | "center";
  tone?: "ink" | "inverse";
  /** Opt out of the entrance stagger — for a heading that swaps in mid-page. */
  animate?: boolean;
  className?: string;
  id?: string;
}

/**
 * The lines settle in one after another rather than together, which is the
 * gesture kuebefarm.lu uses on its own section titles.
 *
 * 70ms is the same step the landing hero already uses, and `fade-up` is the
 * same keyframe — one entrance animation across the site, not two that nearly
 * match. Reduced motion is handled globally in `main.scss`, which flattens
 * every animation to 0.01ms, so there is no check for it here.
 */
const STAGGER_MS = 70;
const delay = (step: number, on: boolean) =>
  on && step > 0 ? { animationDelay: `${step * STAGGER_MS}ms` } : undefined;

/**
 * Left-aligned by default and deliberately so — centred headings on every
 * band is what makes a page look templated. `center` exists for the hero and
 * for genuine full-width statements.
 */
const SectionHeading = ({
  eyebrow,
  title,
  lead,
  action,
  as: Tag = "h2",
  align = "left",
  tone = "ink",
  animate = true,
  className,
  id,
}: SectionHeadingProps) => (
  <div
    className={cn(
      "flex w-full flex-col gap-8 sm:flex-row sm:items-end sm:justify-between",
      align === "center" && "sm:flex-col sm:items-center",
      className
    )}
  >
    <div
      className={cn(
        "flex max-w-2xl flex-col gap-5",
        align === "center" && "items-center text-center"
      )}
    >
      {eyebrow ? (
        <Eyebrow
          tone={tone === "inverse" ? "inverse" : "muted"}
          className={cn(animate && "animate-fade-up")}
        >
          {eyebrow}
        </Eyebrow>
      ) : null}
      <Tag
        id={id}
        // The title always takes the second step, whether or not an eyebrow
        // is above it — the rhythm should not change because a band omits one.
        style={delay(1, animate)}
        className={cn(
          animate && "animate-fade-up",
          tone === "inverse" ? "text-kf-white" : "text-kf-ink"
        )}
      >
        {title}
      </Tag>
      {lead ? (
        <p
          style={delay(2, animate)}
          className={cn(
            "text-body font-light",
            animate && "animate-fade-up",
            tone === "inverse" ? "text-kf-white/75" : "text-kf-body"
          )}
        >
          {lead}
        </p>
      ) : null}
    </div>
    {action ? (
      <div
        style={delay(3, animate)}
        className={cn("shrink-0", animate && "animate-fade-up")}
      >
        {action}
      </div>
    ) : null}
  </div>
);

export { SectionHeading };
